/**
 * W4 — Auto-approval rule builder API client.
 *
 * Thin fetch wrapper matching the `audit-api.ts` / `stats-api.ts` conventions
 * (RTK Query is intentionally NOT used here for consistency with the rest of
 * the Phase 8 / agency surface area). All requests use the agency Bearer token
 * sourced from Redux (`state.auth.agency.accessToken`).
 *
 * Backend contract (LIVE, deployed):
 *   GET    /api/v1/agency/auto-approval-rules?shop={slug}
 *     → { data: AutoApprovalRule[] }
 *   POST   /api/v1/agency/auto-approval-rules
 *     body { name, conditions:[{metric,operator,value}], platform?, tool_name?,
 *            action_type_id?, max_risk_level?, max_daily_auto_approvals?,
 *            is_active?, shop? }  (omit `shop` = org-wide default) → 201 the rule
 *   PATCH  /api/v1/agency/auto-approval-rules/{id}  (partial) → updated rule
 *   DELETE /api/v1/agency/auto-approval-rules/{id} → { success }
 *   GET    /api/v1/agency/auto-approval-rules/recent-matches?shop={slug}&limit=
 *     → { data: RecentMatch[] }
 *
 * Backend enforces `proposals.approve` for mutations (owner/team_lead/manager);
 * the API 403s other roles. The FE gates the affordances regardless.
 *
 * Responses are unwrapped defensively: the backend may return the payload bare
 * OR wrapped under a top-level `{ data }` envelope. `unwrap`/`unwrapList`
 * handle both.
 */
import { CENTRAL_API_BASE_URL } from '../../constants/central-api';
import store from '../../../store/store';

// ---------- shared types ----------

/** KPI metrics a condition can test. */
export type RuleMetric =
  | 'change_percentage'
  | 'budget_delta'
  | 'roas'
  | 'spend'
  | 'risk_level';

/** Comparison operators a condition can apply. */
export type RuleOperator = '<' | '<=' | '>' | '>=' | '==';

/** Risk levels a rule may auto-approve up to. `critical` never auto-approves. */
export type RuleRiskLevel = 'low' | 'medium' | 'high';

/** A single KPI threshold. `value` is a number for numeric metrics and a
 * risk-level string when `metric === 'risk_level'`. */
export interface RuleCondition {
  metric: RuleMetric;
  operator: RuleOperator;
  value: number | string;
}

/** Where a rule applies: an org-wide default or a single shop. */
export type RuleScope = 'org_default' | 'shop';

export interface AutoApprovalRule {
  id: string;
  shop_id: number | null;
  scope: RuleScope;
  platform: string | null;
  tool_name: string | null;
  action_type_id: string | null;
  name: string;
  conditions: RuleCondition[];
  max_risk_level: RuleRiskLevel | null;
  max_daily_auto_approvals: number | null;
  daily_auto_approvals_used: number;
  is_active: boolean;
  created_by_employee_id: string | null;
  created_at: string;
  updated_at: string;
  /**
   * Whether the caller may edit and delete this rule. False on an
   * organisation-wide rule for a member who cannot access every brand.
   * Absent on an older API: treat that as true (see `canManageRule`).
   */
  can_manage?: boolean;
}

/**
 * The 403 code for creating, editing or deleting an organisation-wide rule
 * without access to every brand of the organisation.
 */
export const ORG_WIDE_RULE_REQUIRES_ALL_BRANDS =
  'org_wide_rule_requires_all_brands';

/** What a failed request throws: the server message, status and error code. */
export type AutoApprovalApiError = Error & { status?: number; code?: string };

/** A rule the caller may edit and delete; a missing flag counts as yes. */
export const canManageRule = (rule: { can_manage?: boolean }): boolean =>
  rule.can_manage !== false;

/** Body for create. `shop` omitted = org-wide default. */
export interface CreateAutoApprovalRuleInput {
  name: string;
  conditions: RuleCondition[];
  platform?: string | null;
  tool_name?: string | null;
  action_type_id?: string | null;
  max_risk_level?: RuleRiskLevel | null;
  max_daily_auto_approvals?: number | null;
  is_active?: boolean;
  /** Slug. Omit (or undefined) for an org-wide default rule. */
  shop?: string;
}

/** Body for PATCH — every field optional (partial update). */
export type UpdateAutoApprovalRuleInput = Partial<CreateAutoApprovalRuleInput>;

/** A row from the recent-matches feed — a proposal that a rule auto-approved. */
export interface RecentMatch {
  proposal_id: string;
  rule_id: string;
  auto_approved_at: string;
  actor: string;
  shop: { slug?: string; name?: string } | null;
  platform: string;
  category: string;
  tool_name: string | null;
  entity_name: string | null;
  change_percentage: number | null;
  risk_level: string;
  status: string;
}

// ---------- internal helpers ----------

function buildHeaders(token: string | undefined): Record<string, string> {
  const headers: Record<string, string> = {
    Accept: 'application/json',
  };
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  return headers;
}

async function parseJson<T>(response: Response): Promise<T> {
  const text = await response.text();
  if (!text) {
    return {} as T;
  }
  try {
    return JSON.parse(text) as T;
  } catch (err) {
    // eslint-disable-next-line no-console
    console.error('[auto-approval-api] failed to parse JSON', err, text);
    throw new Error('Invalid response from server.');
  }
}

async function request<T>(
  path: string,
  token: string | undefined,
  init: RequestInit = {}
): Promise<T> {
  const headers: Record<string, string> = {
    ...buildHeaders(token),
    ...((init.headers as Record<string, string>) || {}),
  };
  if (init.body && !headers['Content-Type']) {
    headers['Content-Type'] = 'application/json';
  }

  const response = await fetch(`${CENTRAL_API_BASE_URL}${path}`, {
    ...init,
    headers,
  });

  if (!response.ok) {
    let detail = '';
    let code = '';
    try {
      const body = await parseJson<{
        message?: string;
        error?: string;
        data?: { error?: string } | null;
      }>(response);
      detail = body?.message || body?.error || '';
      // The machine-readable code sits in `data.error` of the envelope.
      const raw = body?.data?.error ?? body?.error;
      code = typeof raw === 'string' ? raw : '';
    } catch {
      // swallow — fall back to status text
    }
    const err: AutoApprovalApiError = new Error(
      detail || `Request failed (${response.status} ${response.statusText})`
    );
    err.status = response.status;
    if (code) err.code = code;
    throw err;
  }

  return parseJson<T>(response);
}

/** Unwrap a possibly-`{data}`-wrapped single object. */
function unwrap<T>(raw: T | { data?: T }): T {
  if (
    raw &&
    typeof raw === 'object' &&
    'data' in (raw as Record<string, unknown>) &&
    (raw as { data?: T }).data !== undefined
  ) {
    return (raw as { data: T }).data;
  }
  return raw as T;
}

/** Normalise a list response to a bare array, accepting bare or `{data}`. */
function unwrapList<T>(raw: T[] | { data?: T[] } | null | undefined): T[] {
  if (Array.isArray(raw)) return raw;
  const data = (raw as { data?: T[] } | null | undefined)?.data;
  return Array.isArray(data) ? data : [];
}

function agencyToken(token?: string): string {
  return token ?? store.getState().auth.agency.accessToken;
}

function shopQuery(shop?: string, extra?: Record<string, string>): string {
  const params = new URLSearchParams();
  if (shop) params.set('shop', shop);
  if (extra) {
    Object.entries(extra).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== '') params.set(k, v);
    });
  }
  const qs = params.toString();
  return qs ? `?${qs}` : '';
}

// ---------- public API ----------

/**
 * List auto-approval rules visible for `shop` (the active shop's rules + any
 * org-wide defaults). Omitting `shop` lists org defaults only.
 */
export async function listAutoApprovalRules(
  shop?: string,
  token?: string
): Promise<AutoApprovalRule[]> {
  const raw = await request<AutoApprovalRule[] | { data?: AutoApprovalRule[] }>(
    `agency/auto-approval-rules${shopQuery(shop)}`,
    agencyToken(token),
    { method: 'GET' }
  );
  return unwrapList<AutoApprovalRule>(raw);
}

/**
 * Create a rule. Omit `shop` in the body for an org-wide default. Returns the
 * created rule (201).
 */
export async function createAutoApprovalRule(
  input: CreateAutoApprovalRuleInput,
  token?: string
): Promise<AutoApprovalRule> {
  const raw = await request<AutoApprovalRule | { data?: AutoApprovalRule }>(
    `agency/auto-approval-rules`,
    agencyToken(token),
    { method: 'POST', body: JSON.stringify(input) }
  );
  return unwrap<AutoApprovalRule>(raw);
}

/**
 * Partially update a rule. Returns the updated rule.
 */
export async function updateAutoApprovalRule(
  id: string,
  input: UpdateAutoApprovalRuleInput,
  token?: string
): Promise<AutoApprovalRule> {
  const raw = await request<AutoApprovalRule | { data?: AutoApprovalRule }>(
    `agency/auto-approval-rules/${encodeURIComponent(id)}`,
    agencyToken(token),
    { method: 'PATCH', body: JSON.stringify(input) }
  );
  return unwrap<AutoApprovalRule>(raw);
}

/**
 * Delete a rule. Resolves true on success.
 */
export async function deleteAutoApprovalRule(
  id: string,
  token?: string
): Promise<boolean> {
  const raw = await request<{ success?: boolean } | null>(
    `agency/auto-approval-rules/${encodeURIComponent(id)}`,
    agencyToken(token),
    { method: 'DELETE' }
  );
  // 204 / empty body → treat as success; otherwise honour `success`.
  if (!raw || typeof raw !== 'object') return true;
  return raw.success !== false;
}

/**
 * Fetch the most recent proposals auto-approved by rules for `shop`. So users
 * can SEE rules working.
 */
export async function listRecentMatches(
  shop?: string,
  limit?: number,
  token?: string
): Promise<RecentMatch[]> {
  const extra = limit && limit > 0 ? { limit: String(limit) } : undefined;
  const raw = await request<RecentMatch[] | { data?: RecentMatch[] }>(
    `agency/auto-approval-rules/recent-matches${shopQuery(shop, extra)}`,
    agencyToken(token),
    { method: 'GET' }
  );
  return unwrapList<RecentMatch>(raw);
}
